import React, { useEffect, useState, useCallback, useMemo } from 'react';
import type { ScheduleItem, Tentor, Branch, ClassRoom } from '../../types/db';
import { UserRole } from '../../types/db';
import {
  Calendar,
  Plus,
  Trash2,
  X,
  Loader2,
  Save,
  Clock,
  Download,
  Users,
  Layers,
  CalendarDays,
  Info,
  LayoutGrid,
  Filter,
  HelpCircle,
  Edit3,
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../context/AuthContext';

interface ScheduleManagerProps {
  branchId?: string;
  tentors: Tentor[];
}

export function ScheduleManager({ branchId, tentors }: ScheduleManagerProps) {
  const { profile: currentUser } = useAuth();
  const [schedules, setSchedules] = useState<ScheduleItem[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [classes, setClasses] = useState<ClassRoom[]>([]);
  const [availableQuizzes, setAvailableQuizzes] = useState<any[]>([]);

  // State Filter
  const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7));

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingSessionKey, setEditingSessionKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [tentorFile, setTentorFile] = useState<File | null>(null);
  const [studentFile, setStudentFile] = useState<File | null>(null);

  const canEdit =
    currentUser &&
    [
      UserRole.MASTER_ADMIN,
      UserRole.SUPER_ADMIN,
      UserRole.ACADEMIC,
      UserRole.CENTRAL_ADMIN,
    ].includes(currentUser.role);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: sData, error: sError } = await supabase
        .from('schedules')
        .select('*, class_room:classes(*)')
        .order('date', { ascending: true });

      if (sError) throw sError;

      if (sData) {
        let filtered = sData;
        if (branchId && branchId !== 'ALL')
          filtered = sData.filter((s) => s.branch_id === branchId);
        setSchedules(filtered);
      }

      const [bRes, cRes, qRes] = await Promise.all([
        supabase.from('branches').select('*'),
        supabase.from('classes').select('*, grade_level:grade_levels(*), major:majors(*)'),
        supabase.from('exams').select('*').eq('exam_type', 'DAILY_QUIZ'),
      ]);

      if (bRes.data) setBranches(bRes.data.filter((b) => b.id !== 'HQ' && b.id !== 'HEADQUARTERS'));

      if (cRes.data) {
        let filteredC = cRes.data;
        if (branchId && branchId !== 'ALL')
          filteredC = cRes.data.filter((c) => c.branch_id === branchId);
        setClasses(filteredC);
      }

      if (qRes.data) setAvailableQuizzes(qRes.data);
    } catch (err: any) {
      /* handled silently */
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const groupedSchedules = useMemo(() => {
    const groups: Record<string, any> = {};

    const filteredByMonth = schedules.filter((s) => {
      if (!s.date) return false;
      return s.date.startsWith(filterMonth);
    });

    filteredByMonth.forEach((s) => {
      const sessionKey = `${s.teacher_user_id || s.teacher}_${s.date}_${s.time}_${s.subject}`;

      if (!groups[sessionKey]) {
        groups[sessionKey] = {
          ...s,
          class_list: [s.class_room?.name || s.grade],
          class_ids: [s.class_id],
          linked_ids: [s.id],
        };
      } else {
        if (!groups[sessionKey].class_list.includes(s.class_room?.name || s.grade)) {
          groups[sessionKey].class_list.push(s.class_room?.name || s.grade);
          groups[sessionKey].class_ids.push(s.class_id);
        }
        groups[sessionKey].linked_ids.push(s.id);
      }
    });

    return Object.values(groups);
  }, [schedules, filterMonth]);

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    day: 'Senin',
    time: '16:00',
    subject: '',
    class_ids: [] as string[],
    teacherId: '',
    teacher: '',
    branchId: '',
    exam_id: '',
  });

  useEffect(() => {
    if (isFormOpen && !isEditing) {
      const initialBranch =
        currentUser?.role === UserRole.ACADEMIC
          ? currentUser.branch_id
          : branchId && branchId !== 'ALL'
            ? branchId
            : currentUser?.branch_id || '';

      setFormData((prev) => ({ ...prev, branchId: initialBranch || '', class_ids: [], exam_id: '', subject: '', teacherId: '' }));
    }
  }, [isFormOpen, isEditing, branchId, currentUser]);

  useEffect(() => {
    const fetchClassesForBranch = async () => {
      if (!formData.branchId || formData.branchId === 'ALL') return;
      const { data } = await supabase
        .from('classes')
        .select('*, grade_level:grade_levels(*), major:majors(*)')
        .eq('branch_id', formData.branchId);

      if (data) setClasses(data);
    };
    fetchClassesForBranch();
  }, [formData.branchId]);

  useEffect(() => {
    if (formData.date) {
      const days = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
      const dayName = days[new Date(formData.date).getDay()];
      setFormData((prev) => ({ ...prev, day: dayName }));
    }
  }, [formData.date]);

  const uploadFile = async (file: File, type: 'tentor' | 'student', targetBranch: string) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${type}_${Date.now()}.${fileExt}`;
    const filePath = `schedule-materials/${targetBranch}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('learning-files')
      .upload(filePath, file, {
        contentType: file.type,
        upsert: true,
      });

    if (uploadError) throw uploadError;
    const {
      data: { publicUrl },
    } = supabase.storage.from('learning-files').getPublicUrl(filePath);
    return publicUrl;
  };

  const addClass = (id: string) => {
    if (!id) return;
    if (formData.class_ids.includes(id)) return;
    setFormData((prev) => ({ ...prev, class_ids: [...prev.class_ids, id] }));
  };

  const removeClass = (id: string) => {
    setFormData((prev) => ({ ...prev, class_ids: prev.class_ids.filter((x) => x !== id) }));
  };

  const handleOpenEdit = (group: any) => {
    setIsEditing(true);
    setEditingSessionKey(`${group.teacher_user_id || group.teacher}_${group.date}_${group.time}_${group.subject}`);
    
    const matchedTentor = tentors.find(
      (t) => t.id === group.teacher_user_id || t.user_id === group.teacher_user_id || t.name === group.teacher_name
    );

    setFormData({
      date: group.date || new Date().toISOString().split('T')[0],
      day: group.day || 'Senin',
      time: group.time || '16:00',
      subject: group.subject || '',
      class_ids: group.class_ids || [],
      teacherId: matchedTentor?.id || matchedTentor?.user_id || '',
      teacher: group.teacher_name || group.teacher || '',
      branchId: group.branch_id || '',
      exam_id: group.exam_id || '',
    });
    setIsFormOpen(true);
  };

  const handleSubmit = async () => {
    const effectiveBranchId =
      currentUser?.role === UserRole.ACADEMIC ? currentUser.branch_id : formData.branchId;

    if (!effectiveBranchId || effectiveBranchId === 'ALL') {
      return window.alert('Silakan pilih cabang spesifik untuk menyimpan jadwal!');
    }

    if (
      !formData.subject ||
      !formData.teacherId ||
      formData.class_ids.length === 0 ||
      !formData.date
    ) {
      return window.alert(
        'MOHON LENGKAPI SEMUA DATA: Mata Pelajaran, Tentor, Pilih Minimal Satu Kelas, dan Tanggal!'
      );
    }

    setIsUploading(true);
    try {
      let tentorUrl = '';
      let studentUrl = '';
      if (tentorFile) tentorUrl = await uploadFile(tentorFile, 'tentor', effectiveBranchId);
      if (studentFile) studentUrl = await uploadFile(studentFile, 'student', effectiveBranchId);

      const selectedTentor = tentors.find(
        (t) => t.id === formData.teacherId || t.user_id === formData.teacherId
      );

      if (isEditing && editingSessionKey) {
        const oldGroup = groupedSchedules.find(
          (g: any) => `${g.teacher_user_id || g.teacher}_${g.date}_${g.time}_${g.subject}` === editingSessionKey
        );
        if (oldGroup && oldGroup.linked_ids) {
          await supabase.from('schedules').delete().in('id', oldGroup.linked_ids);
        }
      }

      // Buat batch ID bersama agar sesi gabungan dihitung sebagai 1 sesi honor tentor
      const batchGroupId = 'BATCH-' + Math.random().toString(36).substr(2, 9);

      const inserts = formData.class_ids.map((classId, index) => {
        const selectedClass = classes.find((c) => c.id === classId);
        return supabase.from('schedules').insert({
          id: 'SCH-' + Math.random().toString(36).substr(2, 9),
          branch_id: effectiveBranchId,
          class_id: classId,
          date: formData.date,
          day: formData.day,
          time: formData.time,
          subject: formData.subject.toUpperCase(),
          grade: selectedClass?.name || 'Class',
          teacher: selectedTentor?.name || 'Unknown',
          teacher_name: selectedTentor?.name || 'Unknown',
          teacher_user_id: selectedTentor?.user_id,
          room: 'Cloud Class',
          tentor_material_url: tentorUrl || null,
          teacher_material_url: tentorUrl || null,
          student_material_url: studentUrl || null,
          exam_id: formData.exam_id || null,
          is_main_batch: index === 0,
          batch_group_id: batchGroupId,
        });
      });

      const results = await Promise.all(inserts);
      const errors = results.filter((r) => r.error);

      if (errors.length > 0) {
        throw new Error(errors[0].error?.message || 'Gagal menyimpan beberapa entri jadwal.');
      }

      window.alert(`Berhasil! Sesi Gabungan (${formData.class_ids.length} Kelas) Terbit sebagai 1 Sesi.`);
      setIsFormOpen(false);
      setIsEditing(false);
      setEditingSessionKey(null);
      setTentorFile(null);
      setStudentFile(null);
      setFormData({ ...formData, subject: '', teacherId: '', teacher: '', class_ids: [], exam_id: '' });

      setFilterMonth(formData.date.slice(0, 7));
      loadData();
    } catch (err: any) {
      window.alert('Gagal Menyimpan Jadwal: ' + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteBatch = async (ids: string[]) => {
    if (!window.confirm(`Hapus seluruh sesi gabungan ini (${ids.length} kelas)?`)) return;
    try {
      const { error } = await supabase.from('schedules').delete().in('id', ids);
      if (error) throw error;
      loadData();
    } catch (err: any) {
      window.alert(err.message);
    }
  };

  const filteredSchedules =
    currentUser?.role === UserRole.TENTOR
      ? groupedSchedules.filter((s: any) => s.teacher_user_id === currentUser.id)
      : groupedSchedules;

  return (
    <div className="space-y-6 animate-fade-in font-sans">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-indigo-600 text-white rounded-2xl flex items-center justify-center shadow-xl shadow-indigo-100">
            <Calendar className="w-8 h-8" />
          </div>
          <div>
            <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight leading-none">
              Manajemen Sesi & Daily Quiz
            </h2>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-2">
              Dukungan Sesi Gabungan & Pengikatan Kuis Harian
            </p>
          </div>
        </div>
        <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
          <div className="bg-white p-1.5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-2">
            <div className="pl-3 py-2">
              <Filter className="w-3.5 h-3.5 text-indigo-400" />
            </div>
            <input
              type="month"
              className="bg-transparent border-none outline-none text-[10px] font-black uppercase text-slate-700 pr-3 cursor-pointer"
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
            />
          </div>

          {canEdit && (
            <button
              onClick={() => {
                setIsEditing(false);
                setEditingSessionKey(null);
                setIsFormOpen(true);
              }}
              className="bg-indigo-600 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl hover:bg-indigo-700 transition-all flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" /> Buat Sesi Gabungan
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-[3rem] shadow-sm border border-slate-100 overflow-hidden relative">
        {loading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-10 flex items-center justify-center">
            <Loader2 className="animate-spin text-indigo-600 w-10 h-10" />
          </div>
        )}

        <div className="overflow-x-auto no-scrollbar">
          <table className="w-full text-sm text-slate-800 min-w-[900px]">
            <thead className="bg-slate-50 border-b text-slate-400 font-black uppercase tracking-widest text-[9px] md:text-[10px]">
              <tr>
                <th className="p-8 text-left">Waktu & Sesi</th>
                <th className="p-8 text-left">Mata Pelajaran & Daftar Kelas</th>
                <th className="p-8 text-left">Tentor & Materi / Kuis</th>
                <th className="p-8 text-center">Opsi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredSchedules.map((s) => {
                const linkedQuiz = availableQuizzes.find((q) => q.id === s.exam_id);
                return (
                  <tr key={s.id} className="hover:bg-indigo-50/20 transition-all font-bold group">
                    <td className="p-8">
                      <div className="text-indigo-600 font-black text-base uppercase">
                        {s.date
                          ? new Date(s.date).toLocaleDateString('id-ID', {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric',
                            })
                          : s.day}
                      </div>
                      <div className="text-xs text-gray-400 font-mono mt-1 flex items-center gap-2">
                        <Clock className="w-3 h-3 text-indigo-300" /> {s.time} WIB
                      </div>
                    </td>
                    <td className="p-8">
                      <div className="uppercase tracking-tight text-slate-900 text-base flex items-center gap-2">
                        {s.subject}
                        {s.linked_ids.length > 1 && (
                          <span className="bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-lg text-[8px] font-black uppercase">
                            GABUNGAN ({s.linked_ids.length} Kelas = 1 Sesi)
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2.5">
                        {s.class_list.map((cls: string, i: number) => (
                          <div
                            key={i}
                            className="px-2 py-1 bg-slate-50 text-slate-500 rounded-lg text-[8px] font-black uppercase border border-slate-100 flex items-center gap-1.5"
                          >
                            <Layers className="w-2.5 h-2.5" /> {cls}
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="p-8">
                      <div className="text-slate-800 font-black text-sm flex items-center gap-2 uppercase">
                        {s.teacher_name || s.teacher}
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {linkedQuiz && (
                          <span className="px-3 py-1 bg-indigo-50 text-indigo-600 border border-indigo-100 rounded-lg text-[8px] font-black uppercase flex items-center gap-1.5">
                            <HelpCircle className="w-3 h-3" /> Quiz: {linkedQuiz.subject} ({linkedQuiz.questions?.length || 5} Soal)
                          </span>
                        )}
                        {(s.tentor_material_url || s.teacher_material_url) && (
                          <a
                            href={s.tentor_material_url || s.teacher_material_url}
                            target="_blank"
                            rel="noreferrer"
                            className="px-3 py-1 bg-amber-50 text-amber-600 border border-amber-100 rounded-lg text-[8px] font-black uppercase flex items-center gap-1.5 hover:bg-amber-100 transition-all"
                          >
                            <Download className="w-3 h-3" /> Materi Tentor
                          </a>
                        )}
                        {s.student_material_url && (
                          <a
                            href={s.student_material_url}
                            target="_blank"
                            rel="noreferrer"
                            className="px-3 py-1 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-lg text-[8px] font-black uppercase flex items-center gap-1.5 hover:bg-emerald-100 transition-all"
                          >
                            <Download className="w-3 h-3" /> Materi Siswa
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="p-8 text-center">
                      {canEdit ? (
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleOpenEdit(s)}
                            className="p-3 bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white rounded-2xl transition-all shadow-sm"
                            title="Ubah Jadwal / Ganti Tentor"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteBatch(s.linked_ids)}
                            className="p-3 bg-rose-50 text-rose-300 hover:text-rose-600 hover:bg-rose-100 rounded-2xl transition-all shadow-sm"
                            title="Hapus Sesi"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <Info className="w-4 h-4 text-slate-100 mx-auto" />
                      )}
                    </td>
                  </tr>
                );
              })}
              {filteredSchedules.length === 0 && !loading && (
                <tr>
                  <td colSpan={4} className="p-20 text-center">
                    <div className="flex flex-col items-center justify-center opacity-40">
                      <LayoutGrid className="w-16 h-16 text-slate-200 mb-4" />
                      <p className="text-slate-300 font-black uppercase tracking-[0.3em] text-[10px]">
                        Tidak ada jadwal sesi untuk periode{' '}
                        {new Date(filterMonth).toLocaleDateString('id-ID', {
                          month: 'long',
                          year: 'numeric',
                        })}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isFormOpen && (
        <div className="fixed inset-0 bg-slate-900/60 z-[150] flex items-center justify-center p-4 backdrop-blur-md overflow-y-auto no-scrollbar">
          <div className="bg-white rounded-[3.5rem] w-full max-w-2xl p-10 shadow-2xl relative my-8 animate-fade-in-up border border-white max-h-[90vh] overflow-y-auto no-scrollbar">
            <button
              onClick={() => setIsFormOpen(false)}
              className="absolute top-8 right-8 p-2 text-slate-400 hover:text-slate-900 transition-all hover:bg-slate-50 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center mb-10">
              <h3 className="font-black text-2xl uppercase tracking-tighter text-slate-900 leading-none">
                {isEditing ? 'Ubah Sesi & Penugasan Tentor' : 'Setup Sesi Gabungan & Quiz'}
              </h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-2">
                Dihitung sebagai 1 Sesi Honor Tentor (Meskipun Gabungan Banyak Kelas)
              </p>
            </div>

            <div className="space-y-6">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest">
                  Penempatan Cabang
                </label>
                <select
                  disabled={currentUser?.role === UserRole.ACADEMIC || isEditing}
                  className="w-full border-2 border-slate-50 p-4 rounded-2xl font-black bg-slate-50/50 outline-none text-xs appearance-none disabled:opacity-70 disabled:bg-slate-100"
                  value={formData.branchId}
                  onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                >
                  <option value="">-- PILIH UNIT CABANG --</option>
                  {branches
                    .filter((b) => b.is_active !== false || b.id === formData.branchId)
                    .map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name.toUpperCase()}
                        {b.is_active === false ? ' (NONAKTIF)' : ''}
                      </option>
                    ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest flex items-center gap-2">
                    <CalendarDays className="w-3.5 h-3.5" /> Tanggal Pelaksanaan
                  </label>
                  <input
                    type="date"
                    className="w-full border-2 border-slate-50 p-4 rounded-2xl font-black bg-slate-50/50 outline-none text-xs"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest">
                    Jam Mulai (WIB)
                  </label>
                  <input
                    type="time"
                    className="w-full border-2 border-slate-50 p-4 rounded-2xl font-black bg-slate-50/50 outline-none text-xs"
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-3 p-6 bg-slate-50/50 rounded-[2rem] border border-slate-100 shadow-inner">
                <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest flex items-center gap-2">
                  <Users className="w-3.5 h-3.5" /> Tag Kelas yang Digabung (Dihitung 1 Sesi)
                </label>

                <div className="flex flex-wrap gap-2 mb-3">
                  {formData.class_ids.map((id) => {
                    const cls = classes.find((c) => c.id === id);
                    return (
                      <div
                        key={id}
                        className="flex items-center gap-2 bg-indigo-600 text-white pl-4 pr-2 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-tight shadow-md animate-fade-in"
                      >
                        {cls?.name || 'Kelas Terpilih'}
                        <button
                          type="button"
                          onClick={() => removeClass(id)}
                          className="p-1 hover:bg-indigo-500 rounded-lg transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                  {formData.class_ids.length === 0 && (
                    <div className="text-[10px] text-slate-300 font-bold uppercase tracking-widest p-2">
                      Belum ada kelas gabungan
                    </div>
                  )}
                </div>

                <select
                  className="w-full border-2 border-white p-4 rounded-2xl font-black text-indigo-700 bg-white outline-none text-xs appearance-none shadow-sm disabled:opacity-50"
                  onChange={(e) => {
                    addClass(e.target.value);
                    e.target.value = '';
                  }}
                  disabled={!formData.branchId || formData.branchId === 'ALL'}
                >
                  <option value="">+ PILIH KELAS UNTUK DIGABUNG...</option>
                  {classes
                    .filter((c) => !formData.class_ids.includes(c.id))
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name.toUpperCase()} ({c.major?.code || 'REG'})
                      </option>
                    ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest">
                  Mata Pelajaran
                </label>
                <input
                  className="w-full border-2 border-slate-50 p-4 rounded-2xl font-black bg-slate-50/50 outline-none text-xs"
                  placeholder="MISAL: BAHASA INGGRIS"
                  value={formData.subject}
                  onChange={(e) =>
                    setFormData({ ...formData, subject: e.target.value.toUpperCase() })
                  }
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest flex items-center gap-2">
                  <HelpCircle className="w-3.5 h-3.5" /> Pautkan Paket Daily Quiz (Opsional)
                </label>
                <select
                  className="w-full border-2 border-slate-50 p-4 rounded-2xl font-black bg-slate-50/50 outline-none text-xs appearance-none cursor-pointer"
                  value={formData.exam_id}
                  onChange={(e) => setFormData({ ...formData, exam_id: e.target.value })}
                >
                  <option value="">-- TANPA KUIS / BUAT NANTI --</option>
                  {availableQuizzes.map((q) => (
                    <option key={q.id} value={q.id}>
                      {q.subject.toUpperCase()} - {q.topic || 'Kuis Harian'} ({q.date}) • [{q.questions?.length || 5} Soal]
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest flex items-center gap-3">
                  <Users className="w-3 h-3" /> Pilih Tentor Pengampu {isEditing ? '(Ganti jika berhalangan)' : ''}
                </label>
                <select
                  className="w-full border-2 border-slate-50 p-4 rounded-2xl font-black bg-slate-50/50 outline-none text-xs appearance-none"
                  value={formData.teacherId}
                  onChange={(e) => {
                    const t = tentors.find(
                      (x) => x.id === e.target.value || x.user_id === e.target.value
                    );
                    setFormData({ ...formData, teacherId: e.target.value, teacher: t?.name || '' });
                  }}
                >
                  <option value="">-- CARI TENTOR --</option>
                  {tentors.map((t) => (
                    <option key={t.id || t.user_id} value={t.id || t.user_id}>
                      {t.name?.toUpperCase()} • {t.subject}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[8px] font-black text-amber-600 uppercase ml-2 tracking-widest">
                    Materi Tentor Baru
                  </label>
                  <input
                    type="file"
                    className="w-full text-[10px] bg-slate-50 p-2 rounded-xl border border-slate-100"
                    onChange={(e) => setTentorFile(e.target.files?.[0] || null)}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[8px] font-black text-emerald-600 uppercase ml-2 tracking-widest">
                    Materi Siswa Baru
                  </label>
                  <input
                    type="file"
                    className="w-full text-[10px] bg-slate-50 p-2 rounded-xl border border-slate-100"
                    onChange={(e) => setStudentFile(e.target.files?.[0] || null)}
                  />
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={isUploading || formData.class_ids.length === 0}
                className="w-full bg-indigo-600 text-white py-6 rounded-[2.5rem] font-black uppercase tracking-[0.2em] shadow-2xl hover:bg-indigo-700 transition-all flex justify-center items-center gap-4 disabled:opacity-50"
              >
                {isUploading ? (
                  <Loader2 className="animate-spin w-5 h-5" />
                ) : (
                  <Save className="w-5 h-5" />
                )}
                {isEditing ? 'SIMPAN PERUBAHAN JADWAL' : 'TERBITKAN JADWAL GABUNGAN & KUIS'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
